import React from 'react';
import { FEATS } from '../data/feats';
import { SPELLS } from '../data/spells';
import { INSTRUMENT_OPTIONS, TOOL_OPTIONS } from '../data/constants';
import './Feats.css';

const Feats = ({ character, computedFeats, updateFeatChoice }) => {
    // computedFeats is an array of objects: { name, source }
    // We map this back to the full feat object from FEATS to display details
    const activeFeatsList = computedFeats.map(cf => {
        const featData = FEATS.find(f => f.name === cf.name);
        return { ...featData, source: cf.source };
    }).filter(f => f.name);

    // console.log('Feats Debug:', { computedFeats, activeFeatsList });

    const DAMAGE_TYPES = ['Acid', 'Bludgeoning', 'Cold', 'Fire', 'Force', 'Lightning', 'Necrotic', 'Piercing', 'Poison', 'Psychic', 'Radiant', 'Slashing', 'Thunder'];
    const SAVING_THROWS = ['Strength', 'Dexterity', 'Constitution', 'Intelligence', 'Wisdom', 'Charisma'];
    const SKILLS = ['Acrobatics', 'Animal Handling', 'Arcana', 'Athletics', 'Deception', 'History', 'Insight', 'Intimidation', 'Investigation', 'Medicine', 'Nature', 'Perception', 'Performance', 'Persuasion', 'Religion', 'Sleight of Hand', 'Stealth', 'Survival'];

    // Helper to render multi-select dropdowns
    const renderMultiSelect = (feat, choices, type, options, label) => {
        const currentValues = Array.isArray(choices[type]) ? choices[type] : (choices[type] ? [choices[type]] : []);
        const count = feat.count || 1;

        return (
            <div className="feat-config-group">
                <label>{label}:</label>
                <div className="multi-select-container">
                    {Array.from({ length: count }).map((_, idx) => {
                        const val = currentValues[idx] || '';

                        const handleChange = (newValue) => {
                            const newValues = [...currentValues];
                            while (newValues.length <= idx) newValues.push('');
                            newValues[idx] = newValue;
                            updateFeatChoice(feat.name, type, newValues);
                        };

                        const isTakenLocal = (opt) => currentValues.includes(opt) && opt !== val;

                        return (
                            <select
                                key={idx}
                                value={val}
                                onChange={(e) => handleChange(e.target.value)}
                                className="feat-dropdown"
                            >
                                <option value="" disabled>Select {type}...</option>
                                {options.map(opt => (
                                    <option key={opt} value={opt} disabled={isTakenLocal(opt)}>
                                        {opt} {isTakenLocal(opt) ? '(Selected)' : ''}
                                    </option>
                                ))}
                            </select>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="feats-container">
            <h2>Feats</h2>
            {activeFeatsList.length === 0 ? (
                <p>No active feats. Select feats from the Traits tab.</p>
            ) : (
                <div className="feats-section">
                    <h3 className="active-section">Active Feats</h3>
                    <div className="feats-list">
                        {activeFeatsList.map(feat => {
                            const choices = character.featChoices?.[feat.name] || {};

                            // Determine interaction type
                            // Fix: duplicate render for Musician caused by `|| feat.toolOptions` check.
                            // Musician has toolOptions (for instruments) but is interactive: 'instrument'.
                            // Strictly check interactive type to disambiguate.
                            const isTool = feat.interactive === 'tool';
                            const isInstrument = feat.interactive === 'instrument';
                            const isSpell = feat.interactive === 'spell';
                            const isDamage = feat.interactive === 'damageType';
                            const isSave = feat.interactive === 'savingThrow';
                            const isSkill = feat.interactive === 'skill';
                            const isASI = feat.interactive === 'asi' || feat.asiOptions;

                            return (
                                <div key={feat.name} className="feat-card active">
                                    <div className="feat-content-wrapper">
                                        <div className="feat-header">
                                            <h4>{feat.name}</h4>
                                            <span className="feat-source-badge">{feat.source}</span>
                                        </div>
                                        <span className="feat-category detail">{feat.category}</span>
                                        <div className="feat-tooltip">{feat.description}</div>
                                    </div>

                                    {/* Configurable ASI Options */}
                                    {isASI && feat.asiOptions && (
                                        <div className="feat-config">
                                            <label>Ability Bonus:</label>
                                            <select
                                                value={choices.asi || ''}
                                                onChange={(e) => updateFeatChoice(feat.name, 'asi', e.target.value)}
                                                className="feat-dropdown"
                                            >
                                                <option value="" disabled>Select...</option>
                                                {feat.asiOptions.map(opt => (
                                                    <option key={opt} value={opt}>
                                                        {opt.charAt(0).toUpperCase() + opt.slice(1)} +1
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Tool Options */}
                                    {isTool && (
                                        renderMultiSelect(feat, choices, 'tool', feat.toolOptions || TOOL_OPTIONS, 'Tool Proficiency')
                                    )}

                                    {/* Instrument Options */}
                                    {isInstrument && (
                                        renderMultiSelect(feat, choices, 'instrument', feat.toolOptions || INSTRUMENT_OPTIONS, 'Musical Instrument')
                                    )}

                                    {/* Skill Options */}
                                    {isSkill && (
                                        renderMultiSelect(feat, choices, 'skill', feat.options || SKILLS, 'Skill Proficiency')
                                    )}

                                    {/* Damage Type Options */}
                                    {isDamage && (
                                        <div className="feat-config">
                                            <label>Damage Type:</label>
                                            <select
                                                value={choices.damageType || ''}
                                                onChange={(e) => updateFeatChoice(feat.name, 'damageType', e.target.value)}
                                                className="feat-dropdown"
                                            >
                                                <option value="" disabled>Select Type...</option>
                                                {DAMAGE_TYPES.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Saving Throw Options */}
                                    {isSave && (
                                        <div className="feat-config">
                                            <label>Saving Throw:</label>
                                            <select
                                                value={choices.savingThrow || ''}
                                                onChange={(e) => updateFeatChoice(feat.name, 'savingThrow', e.target.value)}
                                                className="feat-dropdown"
                                            >
                                                <option value="" disabled>Select Save...</option>
                                                {SAVING_THROWS.map(opt => (
                                                    <option key={opt} value={opt.toLowerCase()}>{opt}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Spell Options */}
                                    {isSpell && (
                                        <div className="feat-config">
                                            <label>Spell Selection:</label>
                                            {/* Logic to filter spells based on feat requirements would go here. 
                                                For now providing full Level 1/2 spell list as generic fallback or simple text input if too complex? 
                                                Better to show level 1/2 spells. */}
                                            <select
                                                value={choices.spell || ''}
                                                onChange={(e) => updateFeatChoice(feat.name, 'spell', e.target.value)}
                                                className="feat-dropdown"
                                            >
                                                <option value="" disabled>Select Spell...</option>
                                                {SPELLS.filter(s => (s.level === 1 || s.level === 2)).map(opt => (
                                                    <option key={opt.name} value={opt.name}>{opt.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Feats;
